import React, { useState, useRef, useEffect } from 'react';
import { 
    useTranslate
} from '../../../lib';

/**
 * DropdownMenu Component
 * 
 * @param {Object} props
 * @param {Array} props.options - Array of menu options
 * @param {string} props.buttonText - Text to display on the button (default: "Opciones")
 * @param {string} props.buttonColor - Background color of the button (default: "#0B5FA3")
 * @param {string} props.position - Position of the menu: "left" | "right" (default: "right")
 * @param {number} props.minWidth - Minimum width of the dropdown menu (default: 240)
 * @param {React.ReactNode} props.buttonIcon - Custom icon for the button
 * @param {Object} props.buttonStyle - Additional styles for the button
 * @param {Object} props.menuStyle - Additional styles for the dropdown menu
 * 
 * Options format:
 * {
 *   id: string (required),
 *   label: string (required),
 *   icon: string | ReactNode (optional),
 *   action: function (required),
 *   disabled: boolean (optional),
 *   divider: boolean (optional) - adds a divider after this option
 * }
 */
const DropdownMenu = ({
  options = [],
  buttonText = 'Opciones',
  buttonColor = 'white',
  position = 'right',
  minWidth = 50,
  buttonIcon,
  buttonStyle = {},
  menuStyle = {}
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const {t} = useTranslate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleOptionClick = (option) => {
    if (!option.disabled && option.action) {
      setIsOpen(false);
      setTimeout(() => option.action(), 0);
    }
  };

  const getHoverColor = (baseColor) => {
    const color = baseColor.replace('#', '');
    const rgb = parseInt(color, 16);
    const r = Math.max(0, ((rgb >> 16) & 0xff) - 20);
    const g = Math.max(0, ((rgb >> 8) & 0xff) - 20);
    const b = Math.max(0, (rgb & 0xff) - 20);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: buttonColor,
          color: '#4e4e4eff',
          border: 'none',
          borderRadius: '50px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          boxShadow: isOpen ? `0 4px 12px ${buttonColor}40` : '0 2px 4px rgba(0, 0, 0, 0.1)',
          ...buttonStyle
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = getHoverColor(buttonColor);
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = buttonColor;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <span>{buttonText}</span>
        {buttonIcon || (
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{
              width: '20px',
              height: '20px',
              transition: 'transform 0.2s ease',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
            />
          </svg>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            [position]: 0,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            minWidth: `${minWidth}px`,
            zIndex: 70,
            overflow: 'hidden',
            animation: 'slideDown 0.2s ease-out',
            ...menuStyle
          }}
        >
          <style>{`
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>

          <button style={{padding: 10, backgroundColor: '#d9e2f0ff', borderRadius: '0 0 15px 0'}}>
            {t('common.options')}:
          </button>  

          {options.map((option, index) => (
            <React.Fragment key={option.id}>
              <button
                role="menuitem"
                onClick={() => handleOptionClick(option)}
                disabled={option.disabled}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: 'none',
                  backgroundColor: 'white',
                  cursor: option.disabled ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: option.disabled ? '#999' : '#2E2E2E',
                  transition: 'background-color 0.15s ease',
                  borderTop: index > 0 && !options[index - 1]?.divider ? '1px solid #f0f0f0' : 'none',
                  textAlign: 'left',
                  opacity: option.disabled ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!option.disabled) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                {option.icon && (
                  <span style={{ 
                    fontSize: typeof option.icon === 'string' ? '18px' : 'inherit',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {option.icon}
                  </span>
                )}
                <span style={{ flex: 1 }}>{option.label}</span>
              </button>
              {option.divider && (
                <div style={{
                  height: '1px',
                  backgroundColor: '#e0e0e0',
                  margin: '4px 0'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;